import React from 'react';
import { CDataTable } from '@coreui/react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Fonts, Palette } from './utils';
import { useGetConfigs } from '../hooks/useGetConfigs';

const Table = ({
  items,
  loading,
  fields,
  itemsPerPage,
  onRowClick,
  scopedSlots,
  pagination,
  rowHeight,
  useNewStyle = false,
  useThermoStyles = false,
  v3,
  ...rest
}) => {
  const { t } = useTranslation();
  const { isSectionEnabled } = useGetConfigs();

  return (
    <TableStyle
      rowHeight={rowHeight}
      useNewStyle={useNewStyle}
      useThermoStyles={useThermoStyles}
      v3>
      <CDataTable
        noItemsViewSlot={loading ? ' ' : undefined}
        noItemsView={{
          noResults: t('No filtering results available'),
          noItems: isSectionEnabled
            ? t('No items available')
            : t('Feature is disabled for this building')
        }}
        items={isSectionEnabled ? items : []}
        loading={loading}
        fields={fields}
        itemsPerPage={Number(itemsPerPage)}
        hover
        pagination={pagination}
        responsive
        onRowClick={onRowClick}
        scopedSlots={scopedSlots}
        {...rest}
      />
    </TableStyle>
  );
};

const sharedTableStyles = css`
  tr {
    &:hover {
      background-color: ${Palette.GHOST_WHITE};
      cursor: pointer;
    }
  }

  td {
    height: 43px;
    text-align: left;
    vertical-align: middle;
    color: ${Palette.BLACK};
    font-family: ${Fonts.OPEN_SANS_FONT};
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    border-top: none !important;
  }

  td.center-align {
    text-align: center;
  }
`;

const newTableStyles = css`
  ${sharedTableStyles}

  tr {
    max-height: ${({ rowHeight }) => rowHeight || '40px'} !important;

    &:hover {
      background-color: rgba(19, 49, 85, 0.1) !important;
      cursor: pointer;
    }

    ${({ v3 }) =>
      v3 ? `border-bottom: none;` : `border-bottom: ${Palette.STROKE_GREY} solid 0.5px;`}
  }

  td {
    font-size: 12px;
    max-height: ${({ rowHeight }) => rowHeight || '40px'} !important;
    padding: 0 !important;
  }
`;

const thermoStyles = css`
  ${sharedTableStyles}

  tr {
    max-height: ${({ rowHeight }) => rowHeight || '40px'} !important;

    &:hover {
      background-color: rgba(117, 117, 117, 0.2);
      cursor: pointer;
    }
  }

  table {
    table-layout: fixed;
  }

  thead tr:first-child th {
    padding: 0.625rem !important;
  }

  td {
    font-size: 12px;
    max-height: ${({ rowHeight }) => rowHeight || '40px'} !important;
    padding: 0 0.625rem !important;
  }
`;

const oldTableStyles = css`
  ${sharedTableStyles}

  td {
    font-size: 14px;
    height: ${({ rowHeight }) => rowHeight || '56px'} !important;
  }
`;

const TableStyle = styled.div`
  flex-grow: 1;
  overflow: auto;

  h2 {
    font-family: ${Fonts.OPEN_SANS_FONT};
    font-size: 36px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;

    svg {
      display: none;
    }
  }

  table {
    width: 100%;
  }

  thead {
    width: 100%;
    border-bottom: 0.5px solid ${Palette.DISABLED};

    .table-sm {
      &:hover {
        background-color: initial !important;
        cursor: auto;
      }
    }

    tr:first-child {
      border-bottom: none;
      padding: 0;

      th {
        height: 12px;
        width: auto;
        padding: 0.625rem 0;

        align-content: center;
        color: ${Palette.BLACK_50};
        font-family: ${Fonts.OPEN_SANS_FONT};
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: 24px;
        border-bottom: none !important;
        border-top: none !important;
        background-color: ${Palette.WHITE};

        &:after {
          line-height: 60px;
        }

        &:hover {
          cursor: auto;
        }

        svg {
          color: ${Palette.BLACK};
          width: 18px;
          height: 18px;
          align-self: center;
          right: auto;
          top: 45%;
        }
      }
    }
  }

  ${({ useNewStyle, useThermoStyles }) =>
    useNewStyle
      ? css`
          ${newTableStyles}
        `
      : useThermoStyles
      ? css`
          ${thermoStyles}
        `
      : css`
          ${oldTableStyles}
        `}
`;

export default Table;
